import { useQuery } from '@apollo/client';
import { useOneTimeCookie } from '@do/seashell/hooks';
import { deleteCookie, getCookieValue } from '@do/seashell/utils';
import {
  Background,
  Button,
  DOLink,
  Link,
  StepTransitionManager,
  Text,
  style
} from '@do/walrus';
import _get from 'lodash/get';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import logoCreditSrc from '../assets/logo-credit.svg';
import logoEmailSrc from '../assets/logo-email.svg';
import { createClient } from '../client';
import {
  CHURN_SURVEY_URL,
  REGISTER_ENDPOINT,
  FLASH_ERROR_COOKIE_NAME,
  ONBOARDING_ORIGIN_PARAM,
  GITHUB_STUDENT_PACK
} from '../constants';
import copy from '../copy';
import { GetRegistrationInfo } from '../generated-types/GetRegistrationInfo';
import {
  registerReferralCode,
  registerReferralCodeVariables
} from '../generated-types/registerReferralCode';
import useActivationRedirectCookie from '../hooks/use-activation-redirect-cookie';
import useDocumentTitle from '../hooks/use-document-title';
import useGithubStudentCookie from '../hooks/use-github-student-cookie';
import useSegmentTracking from '../hooks/use-segment-tracking';
import useSift from '../hooks/use-sift';
import {
  Variant,
  VariantBackgroundImage,
  VariationTemplates,
  VariantBackgroundColor
} from '../interfaces/variant';
import {
  GET_REGISTRATION_FORM_DATA,
  REGISTER_REFERRAL_CODE
} from '../queries/queries';
import registrationVariants from '../registration-variants';
import { getRegisterRefcode, setRegisterCookie } from '../utils/get-refcode';
import { getSessionData } from '../utils/gql-utils';
import { getQueryParam } from '../utils/query-utils';
import { getRegisterVariant } from '../utils/string-utils';
import {
  BannerType,
  ButtonImage,
  Column,
  Row,
  ErrorBanner,
  FormWrap,
  GithubButton,
  GoogleButton,
  HeaderBanner,
  Loading,
  PaddedContents,
  Wrap,
  WrapSplit
} from './Common';
import Header from './Header';
import InterstitialRegistrationForm from './InterstitialRegistrationForm';
import Metadata from './Metadata';
import { Splash } from './Splash';
import NewButton from './newBrandDesign/Button';
import NewCreditBanner from './newBrandDesign/CreditBanner';
import NewCreditText from './newBrandDesign/CreditText';
import NewDOLink from './newBrandDesign/DoLink';
import NewErrorBanner from './newBrandDesign/ErrorBanner';
import NewFooterWrap from './newBrandDesign/FooterWrap';
import NewFormWrap from './newBrandDesign/FormWrap';
import NewGithubButton from './newBrandDesign/GithubButton';
import NewGoogleButton from './newBrandDesign/GoogleButton';
import NewText from './newBrandDesign/Text';
import NewTitle from './newBrandDesign/Title';
import NewWrapper from './newBrandDesign/Wrapper';

const Title = styled.h4``;
const InsideFormWrapper = styled.div``;

const CreditBanner = styled.div`
  background: ${style.colors.blue.lightest};
  padding: ${style.vars.whitespace.regular} ${style.vars.whitespace.medium};
  margin: -${style.vars.space['4']} 0 ${style.vars.space['4']};
  border-bottom: ${style.vars.borderWidth.base} solid
    ${style.colors.blue.darkest};
`;

const StepPaddedContents = styled(PaddedContents)`
  padding-bottom: ${style.vars.space['2']};
`;

const CreditText = styled(Text)`
  margin: ${style.vars.whitespace.xsmall} 0 0;
  font-size: ${style.vars.fontSize.small};
`;

const CreditImage = styled.img`
  margin: -${style.vars.whitespace.xsmall} ${style.vars.whitespace.small} 0;
  width: ${style.vars.space['4']};
`;

const FooterWrap = styled(Text)`
  margin: ${style.vars.whitespace.regular} 0 0;
  font-size: ${style.vars.fontSize.small};
`;

const client = createClient(REGISTER_ENDPOINT);
const userUUID = getSessionData('ajs_user_id').replace(/(^"|"$)/g, '');
const churnSurvey = userUUID
  ? `${CHURN_SURVEY_URL}?uuid=${userUUID}`
  : CHURN_SURVEY_URL;

const WrapContent: React.FC<{
  children: React.ReactNode;
  data: GetRegistrationInfo | undefined;
  step: number;
  variant?: Variant;
  isNewBrandDesignEnabled?: boolean;
}> = ({ children, data, step, variant, isNewBrandDesignEnabled }) => {
  const isSplitTemplate = variant?.splash?.version === VariationTemplates.SPLIT;
  const WrapComponent = isSplitTemplate ? WrapSplit : Wrap;

  const isNewBrand =
    isNewBrandDesignEnabled &&
    variant?.version === VariationTemplates.NEW_BRAND;
  const FormWrapComponent = isNewBrand ? NewFormWrap : FormWrap;
  const InsideFormWrapComponent = isNewBrand ? NewWrapper : InsideFormWrapper;
  const isCreditBanner = !!_get(data, 'registrationFormData.is_credit');

  return (
    <>
      {isCreditBanner && isNewBrand && (
        <NewCreditBanner>
          <strong>{copy.credit.title}</strong>
          <NewCreditText>{copy.credit.details}</NewCreditText>
        </NewCreditBanner>
      )}
      <WrapComponent>
        <FormWrapComponent
          className={
            isCreditBanner
              ? isSplitTemplate
                ? 'isCreditBannerSplit'
                : 'isCreditBanner'
              : ''
          }
        >
          <InsideFormWrapComponent>
            {isCreditBanner && !isNewBrand && (
              <CreditBanner>
                <strong>
                  {copy.credit.title}
                  <CreditImage src={logoCreditSrc} />
                </strong>
                <CreditText>{copy.credit.details}</CreditText>
              </CreditBanner>
            )}
            <StepTransitionManager currentStepNum={step}>
              <StepPaddedContents>{children}</StepPaddedContents>
            </StepTransitionManager>

            <PaddedContents>
              {!isNewBrand && (
                <FooterWrap>
                  {copy.footer.terms_before_link + ' '}
                  <DOLink path="/legal/terms/">
                    {copy.footer.terms_link_text}
                  </DOLink>
                  {' ' + copy.footer.privacy_before_link + ' '}
                  <DOLink path="/legal/privacy/">
                    {copy.footer.privacy_link_text}
                  </DOLink>
                </FooterWrap>
              )}
            </PaddedContents>
          </InsideFormWrapComponent>
        </FormWrapComponent>
        {isNewBrand && (
          <NewFooterWrap>
            {copy.footer.terms_before_link + ' '}
            <NewDOLink path="/legal/terms/">
              {copy.footer.terms_link_text}
            </NewDOLink>
            {' ' + copy.footer.privacy_before_link + ' '}
            <NewDOLink path="/legal/privacy/">
              {copy.footer.privacy_link_text}
            </NewDOLink>
          </NewFooterWrap>
        )}
      </WrapComponent>
    </>
  );
};

type FlashError = {
  code: string;
  field: string;
};

function Registration({
  isNewBrandDesignEnabled = false
}: {
  isNewBrandDesignEnabled?: boolean;
}): ReactElement {
  const [flashErrorCode, setFlashErrorCode] = useState('');
  const flashError = getCookieValue(FLASH_ERROR_COOKIE_NAME);
  useEffect(() => {
    if (flashError) {
      setFlashErrorCode(((flashError as unknown) as FlashError[])[0]?.code);
      deleteCookie(FLASH_ERROR_COOKIE_NAME);
    }
  }, [flashError]);

  useActivationRedirectCookie();

  const location = useLocation();
  const refcode = getRegisterRefcode(location.search);
  useQuery<registerReferralCode, registerReferralCodeVariables>(
    REGISTER_REFERRAL_CODE,
    {
      variables: { refcode },
      skip: !refcode
    }
  );
  setRegisterCookie(refcode);

  const [step, setStep] = React.useState(0);
  const isUserAccountDeactivated = useOneTimeCookie(
    '_digitalocean_flash_code',
    'user_account_deactivated'
  );

  const onboardingOrigin =
    getQueryParam(ONBOARDING_ORIGIN_PARAM, location.search) || '';
  const isGitHubStudentVariant = onboardingOrigin === GITHUB_STUDENT_PACK;
  useGithubStudentCookie(isGitHubStudentVariant);

  const { loading: isLoadingFormData, data } = useQuery<GetRegistrationInfo>(
    GET_REGISTRATION_FORM_DATA,
    {
      client,
      variables: {
        onboarding_origin: onboardingOrigin,
        redirect_url: getQueryParam('redirect_url', location.search)
      }
    }
  );
  const sessionId = _get(data, 'registrationFormData.session_id');

  let variant: Variant | false;

  if (isGitHubStudentVariant) {
    variant = registrationVariants.githubStudentPack;
  } else if (isNewBrandDesignEnabled && !refcode) {
    variant = getRegisterVariant('newBrand', data);
  } else {
    variant = !isLoadingFormData && getRegisterVariant(refcode, data);
  }
  const isNewBrand =
    isNewBrandDesignEnabled &&
    variant &&
    variant?.version === VariationTemplates.NEW_BRAND;

  const TitleComponent = isNewBrand ? NewTitle : Title;
  const TextComponent = isNewBrand ? NewText : Text;
  const ErrorBannerComponent = isNewBrand ? NewErrorBanner : ErrorBanner;

  useDocumentTitle(_get(variant, 'title'));
  useSift(_get(data, 'registrationFormData.session_id'));
  useSegmentTracking(
    'Registration Variant',
    {
      variantType: _get(variant, 'type')
    },
    isLoadingFormData
  );
  const oauthLinkClickHandler = useCallback((url: string) => {
    return async (e: MouseEvent) => {
      e.preventDefault();
      window.location.assign(url);
    };
  }, []);

  if (!variant) {
    return <Loading />;
  }

  const oauthError = _get(data, 'registrationFormData.oauth_error');
  const googleOauthUrl = `/registrations/google${location.search}`;
  const githubOauthUrl = `/registrations/github${location.search}`;
  const signInUrl = _get(
    data,
    'registrationFormData.marketplace_app.create_app_url'
  );
  const registrationEmail = _get(data, 'registrationFormData.email');

  const OauthSection = (): ReactElement => (
    <>
      <TextComponent>
        {copy.form.oauth.title + ' '}
        <Link forceExternal={true} target="_self" to={googleOauthUrl}>
          {copy.form.oauth.google}
        </Link>
        {' ' + copy.form.or + ' '}
        <Link forceExternal={true} target="_self" to={githubOauthUrl}>
          {copy.form.oauth.github}
        </Link>
        {' ' + copy.form.oauth.instead}
      </TextComponent>
    </>
  );

  let form;
  const errorCode = oauthError || flashErrorCode;
  if (step === 0) {
    const pretext = (
      <>
        {errorCode && (
          <ErrorBannerComponent
            error={copy.errors[errorCode] || copy.errors.unknown}
          />
        )}
        <TitleComponent>{variant.title}</TitleComponent>
        {variant.metadata ? (
          <Metadata variant={variant} data={data} />
        ) : (
          <TextComponent>{variant.subtitle}</TextComponent>
        )}
      </>
    );

    if (variant.type === 'team' && registrationEmail) {
      form = (
        <>
          {pretext}
          <OauthSection />
          <InterstitialRegistrationForm
            sessionId={sessionId as string}
            email={registrationEmail}
            onboardingOrigin={onboardingOrigin}
            variant={variant}
            isNewBrandDesignEnabled={isNewBrandDesignEnabled}
          />
        </>
      );
    } else {
      const GoogleButtonComponent = isNewBrand ? NewGoogleButton : GoogleButton;
      const GithubButtonComponent = isNewBrand ? NewGithubButton : GithubButton;

      form = (
        <>
          {pretext}
          {!isGitHubStudentVariant && (
            <>
              {!isNewBrand ? (
                <Button
                  fullWidth={true}
                  onClick={() => {
                    setStep(1);
                  }}
                >
                  <ButtonImage src={logoEmailSrc} />
                  {copy.form.email_signup_title}
                </Button>
              ) : (
                <NewButton
                  fullWidth={true}
                  onClick={() => {
                    setStep(1);
                  }}
                >
                  {copy.form.email_signup_title}
                </NewButton>
              )}

              <GoogleButtonComponent
                text={copy.form.oauth.register.google_button}
                onClick={oauthLinkClickHandler(googleOauthUrl)}
              />
            </>
          )}

          <GithubButtonComponent
            text={copy.form.oauth.register.github_button}
            onClick={oauthLinkClickHandler(githubOauthUrl)}
          />
        </>
      );
    }
  } else {
    form = (
      <>
        <TitleComponent>{copy.form.email_signup_title}</TitleComponent>
        <OauthSection />
        <InterstitialRegistrationForm
          sessionId={sessionId as string}
          email={registrationEmail}
          onboardingOrigin={onboardingOrigin}
          variant={variant}
          isNewBrandDesignEnabled={isNewBrandDesignEnabled}
        />
      </>
    );
  }

  let registrationBackground: React.ReactNode;

  if (VariationTemplates.SPLIT !== variant?.splash?.version) {
    if (variant && React.isValidElement(variant.background)) {
      registrationBackground = variant.background as React.ReactNode;
    } else if (variant) {
      const variantBackground = variant.background as VariantBackgroundImage;
      registrationBackground = (
        <Background
          horizontalPosition={variantBackground.horizontalPosition}
          verticalPosition={variantBackground.verticalPosition}
          url={variantBackground.image}
        />
      );
    }
  } else {
    registrationBackground = <Splash variant={variant?.splash} />;
  }

  return (
    <>
      <Header
        forceExternal={!!signInUrl}
        url={signInUrl}
        queryString={location.search}
      />
      {isUserAccountDeactivated && (
        <HeaderBanner
          type={BannerType.Success}
          text={copy.deactivation_message}
          linkText={copy.survey_text}
          url={churnSurvey}
        />
      )}
      {variant?.splash?.version === VariationTemplates.SPLIT ? (
        <Row>
          <Column
            style={{
              background: (variant?.background as VariantBackgroundColor)?.color
                ? (variant.background as VariantBackgroundColor).color
                : 'unset'
            }}
          >
            <WrapContent
              data={data}
              step={step}
              variant={variant}
              isNewBrandDesignEnabled={isNewBrandDesignEnabled}
            >
              {form}
            </WrapContent>
          </Column>
          {registrationBackground}
        </Row>
      ) : (
        <>
          {registrationBackground}
          <WrapContent
            data={data}
            step={step}
            variant={variant}
            isNewBrandDesignEnabled={isNewBrandDesignEnabled}
          >
            {form}
          </WrapContent>
        </>
      )}
    </>
  );
}

export default Registration;
